import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import SelectSource from '@intelligence/components/TeaSelector/SelectSource';
import { LANGUAGE_CN } from '@home/_config/config';
import './index.scss';

/** 选择器按钮区域的展示配置 */
interface ISelectorConfig {
  /** 搜索框占位文本的国际化 key */
  searchPlaceholder: string;
  /** 按钮标签文本的国际化 key */
  label: string;
  /** 按钮标签图标类型（SvgIcon type） */
  labelIcon: string;
}

/** 热门标签单条选项数据结构（与 useHotTagsOptions 的 IHotTagOption 保持一致） */
interface IHotTagOption {
  /** 标签名称（通用，接口原始字段） */
  name?: string;
  /** 标签中文名称 */
  name_cn?: string;
  /** 标签英文名称 */
  name_en?: string;
  /** 选项 hover 提示（中文） */
  hover_cn?: string;
  /** 选项 hover 提示（英文） */
  hover_en?: string;
  /** cond.intelligenceFilterTags 对应 key 的原始 value 对象 */
  value: any;
  /** value 的 JSON 序列化字符串，用于接口传参 */
  valueJson?: string;
}

/** HotTagsSource 组件 props */
interface IHotTagsSourceProps {
  className?: string;
  style?: React.CSSProperties;
  /** 选项列表（由 useHotTagsOptions 提供） */
  options?: IHotTagOption[];
  /** 受控模式：当前选中值（name 字符串），由父组件控制 */
  value?: string;
  /** 选项变化时触发，返回当前选中的完整 IHotTagOption */
  onConfirm?: (option: IHotTagOption | undefined) => void;
  /** 自定义选择器配置，会与默认配置合并 */
  selectorConfig?: Partial<ISelectorConfig>;
}

/** 默认选择器按钮配置 */
const DEFAULT_SELECTOR_CONFIG: ISelectorConfig = {
  searchPlaceholder: 'dbi_label_search',
  label: 'dbi_hot_tags',
  labelIcon: 'new-logo',
};

/**
 * @description 热门标签数据来源筛选器
 *
 * 功能：
 * - 整体 hover 时展示筛选器说明提示
 * - 根据当前语言（中/英）显示对应的选项文本（name_cn / name_en）
 * - 选项 hover 时展示对应的 hover 提示（hover_cn / hover_en）
 * - 选项变化时立即触发 onConfirm，返回完整的 IHotTagOption
 */
const HotTagsSource: React.FC<IHotTagsSourceProps> = (props) => {
  const {
    className,
    style,
    options = [],
    value: valueProp,
    selectorConfig,
    onConfirm,
  } = props;

  const { formatMessage, locale } = useIntl();
  const isCn = locale === LANGUAGE_CN;

  // 合并外部传入的配置与默认配置
  const mergedSelectorConfig: ISelectorConfig = {
    ...DEFAULT_SELECTOR_CONFIG,
    ...selectorConfig,
  };

  // 受控值：由外部 value prop 控制，空字符串表示无选中
  const selectedValue = valueProp;

  // 将原始 options 转换为 SelectSource 所需格式
  // text：下拉列表展示内容（带 Bubble hover 提示）
  // buttonText：按钮区域纯文本（不触发 Bubble）
  const normalizedOptions = options.map((item: IHotTagOption) => {
    // 根据语言选择显示文本，优先级：name_cn/en > name > value
    const displayText = isCn
      ? item.name_cn || item.name || item.value
      : item.name_en || item.name || item.value;

    // 根据语言选择 hover 提示
    const hoverText = isCn ? item.hover_cn : item.hover_en;

    return {
      // 用 name 字符串作为 value key，确保 === 比较能正确匹配
      value: item.name,
      text: hoverText ? (
        <span style={{ display: 'inline-block', width: '100%', height: '100%' }}>
          <Bubble dark content={hoverText}>
            {displayText}
          </Bubble>
        </span>
      ) : displayText,
      // button 区域不显示 Bubble，避免频繁触发
      buttonText: displayText,
    };
  });

  /**
   * 选项变化处理
   * 更新内部状态，同时立即触发 onConfirm 返回完整的 IHotTagOption
   */
  const handleChange = (val: string) => {
    if (onConfirm instanceof Function) {
      // 通过 name 找到完整的选项对象回传
      const selectedOption = options.find((item) => item.name === val);
      onConfirm(selectedOption);
    }
  };

  return (
    // 整体筛选器 hover 时展示说明提示（国际化）
    <Bubble content={formatMessage({ id: 'dbi_hot_tags_tooltip' })}>
      <SelectSource
        // 按钮宽度默认 220px，支持外部覆盖
        style={{ width: '220px', ...style }}
        // 下拉弹窗最小宽度与按钮保持一致
        overlayStyle={{ minWidth: style?.width || '220px' }}
        overlayClassName="dbi-hot-tags-source-overlay"
        className={classnames('select-item', 'dbi-hot-tags-source', className)}

        appearance="button"
        searchable
        autoClearSearchValue
        isShowButtonLabel
        // 按钮标签文本（国际化）
        buttonName={formatMessage({ id: mergedSelectorConfig.label })}
        // 按钮标签图标
        buttonIcon={
          <SvgIcon
            className="dbi-hot-tags-source-icon"
            type={mergedSelectorConfig.labelIcon}
          />

        }
        // 搜索框占位文本
        searchPlaceholder={formatMessage({
          id: mergedSelectorConfig.searchPlaceholder,
        })}
        // 未选中时的占位文本
        placeholder={
          <span className="select-button-placeholder">
            {formatMessage({ id: 'dbi_hot_tags_placeholder' })}
          </span>
        }
        options={normalizedOptions}
        value={selectedValue}
        onChange={handleChange}
        size="m"
      />
    </Bubble>
  );
};

export default HotTagsSource;
